import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import Consultation from "@/components/sections/Consultation";
import SEOHead from "@/components/SEOHead";
import { Link } from "@/lib/router-compat";
import {
  RelatedPages,
  allTreatmentsLinks,
  allDecisionLinks,
} from "@/components/RelatedPages";
import { Phone, Calendar, Clock, Plane, Briefcase } from "lucide-react";
const AcceleratedTmsFiveDayWeek = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={
          "What the SAINT® Five-Day Treatment Week Is Like | Neuro Wellness TMS Centers of America"
        }
        description={
          "SAINT is ten sessions per day over five days — not five appointments. Learn what the week involves, from brain scan to follow-up, in plain language. Coral Springs, FL."
        }
        canonical={"/accelerated-tms-five-day-week"}
        keywords={
          "SAINT five day treatment, accelerated TMS schedule, what to expect SAINT TMS, TMS treatment week, SAINT daily sessions"
        }
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          name: "What the Five-Day Week Actually Looks Like",
          description:
            "A practical guide to the SAINT five-day treatment week, including daily schedule, side effects, and logistics.",
          about: [
            "SAINT TMS",
            "Treatment Schedule",
            "Accelerated TMS",
            "Patient Experience",
          ],
          provider: {
            "@id": "https://neurowellnesstms.com/#organization",
          },
        }}
      />

      <Navbar />
      <main className="pt-16 sm:pt-20">
        <section className="relative overflow-hidden bg-gradient-navy py-16 sm:py-20 lg:py-24">
          <div className="pointer-events-none absolute -right-32 -top-24 h-96 w-96 rounded-full bg-accent/15 blur-3xl" />
          <div className="container-px mx-auto max-w-4xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-accent/30 bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent sm:px-4 sm:text-sm">
              Patient's Guide · Part 4
            </span>
            <h1 className="mt-6 font-headings text-[2rem] font-extrabold leading-[1.1] text-white sm:text-3xl md:text-4xl lg:text-[2.75rem]">
              What the Five-Day Week Is Actually Like
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
              It's not five appointments. It's ten sessions a day for five days.
              Here's what to expect — so you can plan with confidence.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
              <a
                href="/#consultation"
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-accent px-6 py-3 text-base font-semibold text-accent-foreground shadow-glow hover:opacity-90"
              >
                Schedule a Free Phone Consultation
              </a>
              <a
                href="tel:9548279793"
                className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/40 bg-transparent px-6 py-3 text-base font-semibold text-white hover:bg-white/10"
              >
                <Phone className="h-5 w-5" />
                Call 954-827-9793
              </a>
            </div>
          </div>
        </section>

        <section className="bg-background py-12 lg:py-16">
          <div className="container-px mx-auto max-w-3xl space-y-10">
            {/* Shape of the week — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <div className="flex items-center gap-3">
                <Calendar className="h-7 w-7 text-accent" />
                <h2 className="font-headings text-2xl font-bold text-primary">
                  The shape of the week
                </h2>
              </div>
              <p>
                <strong className="text-primary">Before day one:</strong> You'll
                have an fMRI scan — about 45 minutes — plus intake and
                screening. The scan usually happens at least a day before
                treatment starts, because the targeting software needs time to
                process.
              </p>
              <p>
                <strong className="text-primary">Days one through five:</strong>{" "}
                Ten treatment sessions per day. Each session is short — about
                ten minutes — but they're spaced about fifty minutes apart. The
                spacing is part of the protocol, not just scheduling. Ten
                sessions at hourly intervals means you're on site for about nine
                or ten hours a day, five days in a row, for about fifty sessions
                total.
              </p>
              <p>
                <strong className="text-primary">After:</strong> Follow-up
                assessments, typically at intervals over the following weeks.
              </p>
            </div>

            {/* What it means practically — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <div className="flex items-center gap-3">
                <Clock className="h-7 w-7 text-accent" />
                <h2 className="font-headings text-2xl font-bold text-primary">
                  What that means for you
                </h2>
              </div>
              <ul className="space-y-3">
                {[
                  "Plan on a full week away. If you're traveling, budget six or seven nights of lodging — you'll want to arrive before the scan and may not want to fly out the same evening you finish.",
                  "The between-session hours are yours, but you're not fully free. Some people bring a laptop and work. Others find they can't concentrate well enough by mid-week. Don't schedule anything you can't cancel.",
                  "Expect some side effects. Scalp discomfort during treatment and headache afterward are common and usually manageable. Fatigue builds up across the week.",
                  "Think about driving. If you're tired or headachy by day three, a long commute each way is worth avoiding.",
                  "Bring someone if you can. It's not medically required, but a week of intensive treatment away from home is easier with company.",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Time off work — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <div className="flex items-center gap-3">
                <Briefcase className="h-7 w-7 text-accent" />
                <h2 className="font-headings text-2xl font-bold text-primary">
                  Time off work
                </h2>
              </div>
              <p>
                This is a full working week, plus travel. If you have FMLA
                eligibility, this is a reasonable use of it. If you're
                self-employed or schedule your work in advance, ask us about
                weekend or non-standard start days — it can make the difference
                between going next month and going in three months.
              </p>
            </div>

            {/* Travel */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <div className="flex items-center gap-3">
                <Plane className="h-7 w-7 text-accent" />
                <h2 className="font-headings text-2xl font-bold text-primary">
                  Traveling to us
                </h2>
              </div>
              <p>
                Many of our patients travel from out of state. Fort
                Lauderdale-Hollywood International Airport is about ten minutes
                east of Coral Springs. We can help with hotel recommendations
                and discounted rates.{" "}
                <Link
                  to="/traveling-for-treatment"
                  className="text-accent hover:underline"
                >
                  Learn more about traveling for treatment →
                </Link>
              </p>
            </div>

            {/* Questions to ask — simplified */}
            <div className="space-y-4 text-[17px] leading-relaxed text-muted-foreground">
              <h2 className="font-headings text-2xl font-bold text-primary">
                Good questions to ask about scheduling
              </h2>
              <ul className="space-y-3">
                {[
                  "Is the fMRI on site or elsewhere?",
                  "How many days between the scan and the first session?",
                  "What happens if I need to miss a session — can it be made up, or does the course restart?",
                  "Do you run weekends?",
                  "What time do sessions start and end?",
                  "Is lunch provided, and is there somewhere to work between sessions?",
                ].map((q) => (
                  <li key={q} className="flex items-start gap-3">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-accent" />
                    <span>{q}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-light-blue bg-light-blue/30 p-6">
              <h3 className="font-headings text-lg font-bold text-primary">
                Continue reading
              </h3>
              <div className="mt-3 flex flex-col gap-2">
                <Link
                  to="/accelerated-tms-other-conditions"
                  className="text-accent hover:underline"
                >
                  Part 5: Can TMS Help With Other Conditions? →
                </Link>
                <Link
                  to="/traveling-for-treatment"
                  className="text-accent hover:underline"
                >
                  Traveling to Us for Treatment →
                </Link>
                <Link
                  to="/accelerated-tms-coverage"
                  className="text-accent hover:underline"
                >
                  Part 3: What Coverage Looks Like →
                </Link>
              </div>
            </div>
          </div>
        </section>

        <RelatedPages
          heading="Related Resources"
          links={[...allTreatmentsLinks, ...allDecisionLinks]}
        />
        <Consultation />
      </main>
      <Footer />
    </div>
  );
};
export default AcceleratedTmsFiveDayWeek;
